import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
    margin-bottom: 50px;
  }

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: 25px;
    position: relative;
    width: 100%;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: 80px auto 0;
  }
`;

const StyledProject = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .project-inner {
        transform: translateY(-10px);
        background-color: rgba(100, 255, 218, 0.05);
        border: 1px solid var(--green);
      }
      .project-title {
        color: var(--green);
      }
    }
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
    padding: 2.5rem 2rem;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    border: 1px solid transparent;
    backdrop-filter: blur(10px);
  }

  .project-index {
    position: absolute;
    top: 20px;
    right: 30px;
    font-family: var(--font-mono);
    font-size: 40px;
    color: rgba(100, 255, 218, 0.1);
    font-weight: 700;
  }

  .project-title {
    margin: 20px 0 15px;
    color: var(--lightest-slate);
    font-size: var(--fz-xxl);
    transition: var(--transition);
  }

  .project-description {
    color: var(--slate);
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 20px;

    p {
      margin: 0;
    }
  }

  .project-tech-list {
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
    flex-wrap: wrap;
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      color: var(--green);
      line-height: 1.75;

      &:not(:last-of-type) {
        margin-right: 15px;
      }
    }
  }

  .book-link {
    margin-top: 30px;
    display: flex;
    align-items: center;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    text-decoration: none;

    svg {
      margin-left: 10px;
      width: 16px;
      height: 16px;
      transition: var(--transition);
    }

    &:hover {
      svg {
        transform: translateX(5px);
      }
    }
  }
`;

const Servicios = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/servicios/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealArchiveLink.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const GRID_LIMIT = 6;
  const projects = data.projects.edges.filter(({ node }) => node);
  const firstSix = projects.slice(0, GRID_LIMIT);
  const projectsToShow = showMore ? projects : firstSix;

  const projectInner = (node, i) => {
    const { frontmatter, html } = node;
    const { title, tech } = frontmatter;

    return (
      <div className="project-inner">
        <span className="project-index">{(i + 1).toString().padStart(2, '0')}</span>
        
        <h3 className="project-title">{title}</h3>

        <div className="project-description" dangerouslySetInnerHTML={{ __html: html }} />

        <footer>
          {tech && (
            <ul className="project-tech-list">
              {tech.map((tech, i) => (
                <li key={i}>{tech}</li>
              ))}
            </ul>
          )}
          
          <a href="/#agenda" className="book-link">
            Reservar ahora <Icon name="External" />
          </a>
        </footer>
      </div>
    );
  };

  return (
    <StyledProjectsSection>
      <a id="servicios" ><br></br></a>
      <a ><br></br></a>
      <a ><br></br></a>
      <a ><br></br></a>
      <h2 className="title">Servicios de Consultoría</h2>

      <ul className="projects-grid">
        {prefersReducedMotion ? (
          <>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <StyledProject key={i}>{projectInner(node, i)}</StyledProject>
              ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <CSSTransition
                  key={i}
                  classNames="fadeup"
                  timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 600 : 300}
                  exit={false}>
                  <StyledProject
                    key={i}
                    ref={el => (revealProjects.current[i] = el)}
                    style={{
                      transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                    }}>
                    {projectInner(node, i)}
                  </StyledProject>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </ul>

      {projects.length > GRID_LIMIT && (
        <button className="more-button" onClick={() => setShowMore(!showMore)}>
          Mostrar {showMore ? 'Menos' : 'Más'}
        </button>
      )}
    </StyledProjectsSection>
  );
};

export default Servicios;
